import { Bell, LogOut, Moon, Save, ShieldCheck, UserRound } from 'lucide-react';
import { FormEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '../components/PageHeader';
import { StatusNotice } from '../components/StatusNotice';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';

interface NotificationPreferences {
  email_enabled: number;
  message_emails: number;
  offer_emails: number;
  viewing_emails: number;
  payment_emails: number;
  placement_emails: number;
  marketing_emails: number;
}

const defaultPreferences: NotificationPreferences = {
  email_enabled: 1,
  message_emails: 1,
  offer_emails: 1,
  viewing_emails: 1,
  payment_emails: 1,
  placement_emails: 1,
  marketing_emails: 0,
};

export function SettingsPage() {
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const [preferences, setPreferences] = useState(defaultPreferences);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get<NotificationPreferences>('/api/account/notification-preferences')
      .then(setPreferences)
      .catch(() => undefined);
  }, []);

  async function submitProfile(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true); setError(''); setNotice('');
    try {
      await api.patch('/api/account/profile', { name: form.get('name'), phone: form.get('phone') || null });
      await refresh();
      setNotice('Profile updated.');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not update profile.');
    } finally { setBusy(false); }
  }

  async function saveNotifications(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true); setError(''); setNotice('');
    try {
      await api.patch('/api/account/notification-preferences', {
        emailEnabled: form.get('emailEnabled') === 'on',
        messageEmails: form.get('messageEmails') === 'on',
        offerEmails: form.get('offerEmails') === 'on',
        viewingEmails: form.get('viewingEmails') === 'on',
        paymentEmails: form.get('paymentEmails') === 'on',
        placementEmails: form.get('placementEmails') === 'on',
        marketingEmails: false,
      });
      setNotice('Notification preferences saved.');
      setPreferences(await api.get('/api/account/notification-preferences'));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not save notification preferences.');
    } finally { setBusy(false); }
  }

  async function signOut() {
    await logout();
    navigate('/');
  }

  return <div className="page narrow-page">
    <PageHeader title="Settings" description="Manage your account, transactional email preferences and appearance." />
    {error && <StatusNotice type="error">{error}</StatusNotice>}
    {notice && <StatusNotice type="success">{notice}</StatusNotice>}

    <form className="settings-list" onSubmit={submitProfile}>
      <article><div><UserRound/><span><strong>Profile</strong><small>Your public name and contact number</small></span></div></article>
      <div className="settings-form">
        <label><span>Name</span><input name="name" defaultValue={user?.name} required/></label>
        <label><span>Email</span><input value={user?.email || ''} disabled/></label>
        <label><span>Phone</span><input name="phone" defaultValue={user?.phone || ''}/></label>
        <button className="primary-button" disabled={busy}><Save size={16}/>{busy ? 'Saving…' : 'Save profile'}</button>
      </div>
    </form>

    <form className="settings-list" onSubmit={saveNotifications}>
      <article><div><Bell/><span><strong>Email notifications</strong><small>Choose which transactional events should also reach your inbox</small></span></div></article>
      <div className="settings-form notification-preferences">
        <Preference name="emailEnabled" label="Enable transactional email" description="Master switch for activity emails" checked={Boolean(preferences.email_enabled)}/>
        <Preference name="messageEmails" label="Unread messages" description="Email me when a conversation remains unread" checked={Boolean(preferences.message_emails)}/>
        <Preference name="offerEmails" label="Hostel offers and matches" description="Open-request matches and structured offers" checked={Boolean(preferences.offer_emails)}/>
        <Preference name="viewingEmails" label="Viewing updates" description="Proposals, changes and reminders" checked={Boolean(preferences.viewing_emails)}/>
        <Preference name="paymentEmails" label="Payment records" description="Formal payment requests and receipt confirmations" checked={Boolean(preferences.payment_emails)}/>
        <Preference name="placementEmails" label="Placement confirmations" description="Claims, confirmations and move-in follow-up" checked={Boolean(preferences.placement_emails)}/>
        <button className="primary-button" disabled={busy}><Save size={16}/>{busy ? 'Saving…' : 'Save notifications'}</button>
      </div>
    </form>

    <div className="settings-list">
      <article><div><Moon/><span><strong>Appearance</strong><small>Light, dark or system theme</small></span></div><span className="muted">Use the top-bar theme control</span></article>
      <article><div><ShieldCheck/><span><strong>Privacy and safety</strong><small>Reports and moderation protect the transaction trail</small></span></div></article>
      <button type="button" className="secondary-button danger full" onClick={() => void signOut()}><LogOut size={17}/> Sign out</button>
    </div>
  </div>;
}

function Preference({ name, label, description, checked }: { name: string; label: string; description: string; checked: boolean }) {
  return <label className="preference-row">
    <span><strong>{label}</strong><small>{description}</small></span>
    <span className="switch"><input type="checkbox" name={name} defaultChecked={checked}/><span/></span>
  </label>;
}
